import { roundMoney, moneyScaleForCurrency, type MoneyScale } from '@shared/money';

const DEFAULT_MONEY_SCALE = moneyScaleForCurrency('ARS');

export type BulkIntegerDirection = 'nearest' | 'up' | 'down';
export type BulkCentsMode = 'step_5' | 'step_10' | 'ending_99';

export type BulkPriceRounding =
  | { kind: 'none' }
  | { kind: 'integer'; direction: BulkIntegerDirection }
  | { kind: 'cents'; mode: BulkCentsMode };

export const DEFAULT_BULK_PRICE_ROUNDING: BulkPriceRounding = { kind: 'none' };

export type BulkRoundingFamily = 'none' | 'integer' | 'cents';

export function bulkRoundingFamily(rounding: BulkPriceRounding): BulkRoundingFamily {
  return rounding.kind;
}

export function bulkIntegerSub(rounding: BulkPriceRounding): BulkIntegerDirection {
  return rounding.kind === 'integer' ? rounding.direction : 'nearest';
}

export function bulkCentsSub(rounding: BulkPriceRounding): BulkCentsMode {
  return rounding.kind === 'cents' ? rounding.mode : 'step_5';
}

export function bulkPriceRoundingFromUi(
  family: BulkRoundingFamily,
  integerDirection: BulkIntegerDirection,
  centsMode: BulkCentsMode,
): BulkPriceRounding {
  if (family === 'integer') return { kind: 'integer', direction: integerDirection };
  if (family === 'cents') return { kind: 'cents', mode: centsMode };
  return { kind: 'none' };
}

function roundToStep(value: number, step: number, scale: MoneyScale): number {
  if (step <= 0 || !Number.isFinite(value)) return roundMoney(value, scale);
  const rounded = Math.round(value / step) * step;
  return roundMoney(rounded, scale);
}

function roundIntegerDirection(value: number, direction: BulkIntegerDirection): number {
  if (!Number.isFinite(value)) return 0;
  if (direction === 'up') return Math.ceil(value - 1e-12);
  if (direction === 'down') return Math.floor(value + 1e-12);
  return Math.round(value);
}

/** Post-coeficiente: % ya aplicado en `rawPrice`. */
export function applyBulkPriceRounding(
  rawPrice: number,
  rounding: BulkPriceRounding,
  scale: MoneyScale = DEFAULT_MONEY_SCALE,
): number {
  if (!Number.isFinite(rawPrice)) return 0;
  if (rounding.kind === 'none') return roundMoney(rawPrice, scale);

  if (rounding.kind === 'integer') {
    const asInt = roundIntegerDirection(rawPrice, rounding.direction);
    return roundMoney(asInt, scale);
  }

  if (scale.fractionDigits <= 0) {
    return roundMoney(roundIntegerDirection(rawPrice, 'nearest'), scale);
  }

  switch (rounding.mode) {
    case 'step_5':
      return roundToStep(rawPrice, 0.05, scale);
    case 'step_10':
      return roundToStep(rawPrice, 0.1, scale);
    case 'ending_99': {
      const whole = Math.floor(rawPrice + 1e-12);
      return roundMoney(whole + 0.99, scale);
    }
    default:
      return roundMoney(rawPrice, scale);
  }
}

export function computeBulkPriceAfterCoefficient(
  currentPrice: number,
  coefficient: number,
  rounding: BulkPriceRounding,
  scale: MoneyScale = DEFAULT_MONEY_SCALE,
): number {
  return applyBulkPriceRounding(currentPrice * coefficient, rounding, scale);
}

/** Una línea para «Listo para revisar» / subtítulo. */
export function describeBulkPriceRounding(rounding: BulkPriceRounding): string | null {
  if (rounding.kind === 'none') return null;
  if (rounding.kind === 'integer') {
    if (rounding.direction === 'up') return 'peso entero hacia arriba';
    if (rounding.direction === 'down') return 'peso entero hacia abajo';
    return 'peso entero cercano';
  }
  if (rounding.mode === 'step_5') return 'centavos de a 5';
  if (rounding.mode === 'step_10') return 'centavos de a 10';
  return 'terminar en ,99';
}

export function bulkPriceRoundingHint(
  family: BulkRoundingFamily,
  applyToPrices: boolean,
): string {
  if (!applyToPrices) return '';
  if (family === 'none') return 'Dejamos el resultado del porcentaje, con centavos.';
  if (family === 'integer') return 'Precios en pesos redondos ($500, $1.200).';
  return 'Ajustá solo los centavos raros después del %.';
}

/** Id estable para select / sheet del modal bulk. */
export type BulkPriceRoundingSelectId =
  | 'none'
  | 'integer_nearest'
  | 'integer_up'
  | 'integer_down'
  | 'cents_step_5'
  | 'cents_step_10'
  | 'cents_ending_99';

export function bulkPriceRoundingToSelectId(rounding: BulkPriceRounding): BulkPriceRoundingSelectId {
  if (rounding.kind === 'none') return 'none';
  if (rounding.kind === 'integer') {
    if (rounding.direction === 'up') return 'integer_up';
    if (rounding.direction === 'down') return 'integer_down';
    return 'integer_nearest';
  }
  if (rounding.mode === 'step_10') return 'cents_step_10';
  if (rounding.mode === 'ending_99') return 'cents_ending_99';
  return 'cents_step_5';
}

export function bulkPriceRoundingFromSelectId(id: BulkPriceRoundingSelectId): BulkPriceRounding {
  switch (id) {
    case 'integer_nearest':
      return { kind: 'integer', direction: 'nearest' };
    case 'integer_up':
      return { kind: 'integer', direction: 'up' };
    case 'integer_down':
      return { kind: 'integer', direction: 'down' };
    case 'cents_step_5':
      return { kind: 'cents', mode: 'step_5' };
    case 'cents_step_10':
      return { kind: 'cents', mode: 'step_10' };
    case 'cents_ending_99':
      return { kind: 'cents', mode: 'ending_99' };
    default:
      return { kind: 'none' };
  }
}

export type BulkPriceRoundingSelectOption = { id: BulkPriceRoundingSelectId; label: string };

export function bulkPriceRoundingSelectOptions(showCentsFamily: boolean): BulkPriceRoundingSelectOption[] {
  const opts: BulkPriceRoundingSelectOption[] = [
    { id: 'none', label: 'Sin redondeo extra' },
    { id: 'integer_nearest', label: 'Peso entero — cercano' },
    { id: 'integer_up', label: 'Peso entero — hacia arriba' },
    { id: 'integer_down', label: 'Peso entero — hacia abajo' },
  ];
  if (showCentsFamily) {
    opts.push(
      { id: 'cents_step_5', label: 'Centavos — de a 5' },
      { id: 'cents_step_10', label: 'Centavos — de a 10' },
      { id: 'cents_ending_99', label: 'Terminar en ,99' },
    );
  }
  return opts;
}

export function bulkPriceRoundingSelectLabel(
  rounding: BulkPriceRounding,
  options: BulkPriceRoundingSelectOption[],
): string {
  const id = bulkPriceRoundingToSelectId(rounding);
  return options.find((o) => o.id === id)?.label ?? options[0]?.label ?? 'Sin redondeo extra';
}
